// Leche de vaca y leche de búfala.
//
// ── Por qué esto no es «un campo más» ──
//
// La búfala rinde mucho más que la vaca: la misma tina da bastante más queso, y
// la leche cuesta más. Si el programa no supiera de cuál es cada cuajada,
// pasarían las dos cosas a la vez y ninguna se vería:
//
//   · toda cuajada de búfala saldría «rindió excelente» y toda la de vaca
//     «rindió mal», aunque las dos estuvieran normales. El aviso de rendimiento
//     dejaría de significar algo y con él el de fincas sospechosas.
//   · la leche de búfala se costearía al precio de la vaca, y el margen del
//     queso de búfala saldría inflado.
//
// Por eso el tipo va en el recibo Y en la cuajada, y cada variedad de queso
// guarda su rendimiento para cada leche.
//
// El cliente hace las cuajadas SEPARADAS —o de vaca o de búfala, nunca
// mezcladas—, y el programa lo exige así. Es lo único que permite medir: una
// mezcla no se puede juzgar contra un rendimiento solo.

export const VACA = 'vaca';
export const BUFALA = 'bufala';

export const TIPOS = [
  { id: VACA,   nombre: 'Vaca',   ico: '🐄' },
  { id: BUFALA, nombre: 'Búfala', ico: '🐃' },
];

// Lo que ya estaba guardado antes de que existiera la búfala es de vaca. No es
// una suposición: cuando se anotó, la búfala no se podía anotar.
export function tipoDe(fila) {
  return fila && fila.tipo_leche === BUFALA ? BUFALA : VACA;
}

export function nombreDelTipo(tipo) {
  const t = TIPOS.find(x => x.id === (tipo === BUFALA ? BUFALA : VACA));
  return t.ico + ' ' + t.nombre;
}

// `Number(null)` es 0, no NaN. Sin el primer renglón, un precio sin pactar se
// volvía un cero válido y la entrega quedaba anotada en $0.
const num = (x) => {
  if (x === null || x === undefined || x === '') return null;
  const n = Number(x);
  return Number.isFinite(n) && n > 0 ? n : null;
};

// El precio pactado con esa finca para ese tipo de leche.
//
// Devuelve null y no cero cuando no hay precio: cero es un precio —«se la
// regala»— y null es «no se ha pactado». Anotar una entrega a cero por un campo
// sin llenar es regalarle la leche a alguien en la contabilidad.
export function precioDe(finca, tipo) {
  if (!finca) return null;
  return tipo === BUFALA ? num(finca.precio_litro_bufala) : num(finca.precio_litro);
}

// Qué tipos de leche vende esta finca: los que tienen precio pactado.
//
// Una finca que solo vende vaca no debe poder anotar una entrega de búfala por
// un dedazo en un desplegable.
export function tiposQueVende(finca) {
  return TIPOS.filter(t => precioDe(finca, t.id) !== null).map(t => t.id);
}

// Cuánto vale esta entrega. Es el número que el finquero quiere oír en el
// momento de entregar, no al final de la quincena.
export function valorDeEntrega({ litros, precio }) {
  const L = num(litros), P = num(precio);
  if (!L || !P) return null;
  return Math.round(L * P);
}

// El rendimiento esperado de esta variedad con esta leche.
//
// Si la variedad no tiene rendimiento pactado para la búfala, se devuelve null
// y NO el de la vaca. Juzgar una cuajada de búfala contra el rendimiento de la
// vaca es peor que no juzgarla: diría «rindió espectacular» siempre.
export function rendimientoDe(producto, tipo) {
  if (!producto) return null;
  return tipo === BUFALA
    ? num(producto.rendimiento_bufala)
    : num(producto.rendimiento_esperado);
}

// Los litros recibidos de cada tipo en una lista de recibos.
export function litrosPorTipo(recibos) {
  const r = { [VACA]: 0, [BUFALA]: 0 };
  for (const x of recibos || []) r[tipoDe(x)] += Number(x.litros) || 0;
  return r;
}

// ── Cuando se mezclan las dos leches en una misma tina ────────────────────
//
// El cliente SÍ las revuelve. Y aquí hay una trampa que cambia el resultado:
//
// El rendimiento se mide en litros por kilo, que es una razón. Promediar dos
// razones ponderando por litros da un número parecido y equivocado:
//
//   400 L de vaca a 7,02 L/kg  y  200 L de búfala a 4,71 L/kg
//     promedio ponderado ...... (400×7,02 + 200×4,71) / 600 = 6,25 L/kg  ❌
//     de verdad ............... 400/7,02 + 200/4,71 = 99,4 kg
//                               600 / 99,4          = 6,03 L/kg  ✅
//
// Lo que se suma son los KILOS que debe dar cada leche, no sus razones. Y el
// error no es inocente: 6,25 es más alto que 6,03, o sea más permisivo, así que
// una cuajada mezclada que rindiera mal pasaría sin aviso. El promedio ingenuo
// esconde justo lo que hay que ver.
export function mezcla({ litrosVaca, litrosBufala }, producto) {
  const lv = Math.max(0, Number(litrosVaca) || 0);
  const lb = Math.max(0, Number(litrosBufala) || 0);
  const total = lv + lb;
  if (total <= 0) return null;

  const rv = rendimientoDe(producto, VACA);
  const rb = rendimientoDe(producto, BUFALA);

  // Falta el rendimiento de una leche que sí entró en la tina. No se juzga
  // nada: media cuenta es peor que ninguna, porque se presenta con la misma
  // confianza que una completa.
  const falta = (lv > 0 && !rv) ? VACA : (lb > 0 && !rb) ? BUFALA : null;
  if (falta) return { total, litrosVaca: lv, litrosBufala: lb, esperado: null, falta };

  const kilosEsperados = (lv > 0 ? lv / rv : 0) + (lb > 0 ? lb / rb : 0);
  return {
    total, litrosVaca: lv, litrosBufala: lb, falta: null,
    kilosEsperados: Math.round(kilosEsperados * 100) / 100,
    esperado: Math.round((total / kilosEsperados) * 100) / 100,
    // Qué tanto de la tina fue búfala. Es lo que explica que el esperado de
    // hoy no sea el de ayer, y sin decirlo el número parece moverse solo.
    parteBufala: Math.round((lb / total) * 100),
  };
}

// El tope de la mezcla, por el mismo camino: los kilos mínimos se suman.
export function topeDeMezcla({ litrosVaca, litrosBufala }, producto) {
  const lv = Math.max(0, Number(litrosVaca) || 0);
  const lb = Math.max(0, Number(litrosBufala) || 0);
  const total = lv + lb;
  if (total <= 0 || !producto) return null;
  const tv = num(producto.rendimiento_maximo) || (num(producto.rendimiento_esperado) * 1.05);
  const tb = num(producto.rendimiento_bufala_maximo) || (num(producto.rendimiento_bufala) * 1.05);
  if ((lv > 0 && !tv) || (lb > 0 && !tb)) return null;
  const kilos = (lv > 0 ? lv / tv : 0) + (lb > 0 ? lb / tb : 0);
  return Math.round((total / kilos) * 100) / 100;
}

// El precio del litro de una mezcla. Éste SÍ es un promedio ponderado normal:
// la plata se suma, no es una razón.
export function precioDeMezcla({ litrosVaca, litrosBufala, precioVaca, precioBufala }) {
  const lv = Math.max(0, Number(litrosVaca) || 0);
  const lb = Math.max(0, Number(litrosBufala) || 0);
  const total = lv + lb;
  if (total <= 0) return null;
  const pv = Number(precioVaca) || 0;
  const pb = Number(precioBufala) || 0;
  if ((lv > 0 && !pv) || (lb > 0 && !pb)) return null;
  return Math.round(((lv * pv + lb * pb) / total) * 100) / 100;
}
